import { useMemo } from 'react';
import dayjs from 'dayjs';
import type { Application } from '../types/application';
import { APPLICATION_STATUS_LIST } from '../../../constants/statusList';

export interface DashboardStats {
  total: number;
  todayCount: number;
  yesterdayCount: number;
  todayChange: number | null;
  thisMonthRevenue: number;
  lastMonthRevenue: number;
  monthRevenueChange: number | null;
  completionRate: number;
  inProgressCount: number;
  avgProcessingDays: number;
  statusDistribution: { name: string; value: number; color?: string }[];
  dailyTrend: { date: string; count: number }[];
  channelBreakdown: { name: string; value: number }[];
  monthlyRevenue: { month: string; total: number }[];
  totalDevices: number;
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return Math.round(((current - previous) / previous) * 100);
}

export const useDashboardStats = (applications: Application[]): DashboardStats => {
  return useMemo(() => {
    if (!applications.length) {
      return {
        total: 0,
        todayCount: 0,
        yesterdayCount: 0,
        todayChange: null,
        thisMonthRevenue: 0,
        lastMonthRevenue: 0,
        monthRevenueChange: null,
        completionRate: 0,
        inProgressCount: 0,
        avgProcessingDays: 0,
        statusDistribution: [],
        dailyTrend: [],
        channelBreakdown: [
          { name: '거래처', value: 0 },
          { name: '기업', value: 0 },
          { name: '일반고객', value: 0 },
        ],
        monthlyRevenue: [],
        totalDevices: 0,
      };
    }

    const now = dayjs();
    const todayStart = now.startOf('day');
    const yesterdayStart = todayStart.subtract(1, 'day');
    const thisMonthStart = now.startOf('month');
    const lastMonthStart = thisMonthStart.subtract(1, 'month');

    let todayCount = 0;
    let yesterdayCount = 0;
    let thisMonthRevenue = 0;
    let lastMonthRevenue = 0;
    let completedCount = 0;
    let inProgressCount = 0;
    let totalDevices = 0;
    let partnerCount = 0;   // 거래처 (source_type: manual | partner)
    let bizCount = 0;       // 기업 (b2b + direct)
    let personalCount = 0;  // 일반고객 (b2c)

    // 평균 처리시간 계산용
    const processingDays: number[] = [];

    // 상태 분포
    const statusMap: Record<string, number> = {};
    APPLICATION_STATUS_LIST.forEach((s) => {
      if (s !== '전체') statusMap[s] = 0;
    });

    // 일별 트렌드 (최근 7일)
    const dailyMap: Record<string, number> = {};
    for (let i = 6; i >= 0; i--) {
      dailyMap[now.subtract(i, 'day').format('MM/DD')] = 0;
    }

    // 월별 매출
    const monthlyMap: Record<string, number> = {};

    applications.forEach((app) => {
      const createdSec = app.created_at?.seconds;
      if (!createdSec) return;

      const created = dayjs.unix(createdSec);
      const finalPrice = Number(app.total_final_price || app.final_price || 0);

      // 오늘 / 어제
      if (created.isAfter(todayStart) || created.isSame(todayStart)) todayCount++;
      if (created.isAfter(yesterdayStart) && created.isBefore(todayStart)) yesterdayCount++;

      // 이번 달 / 지난 달 매출 (완료 건 — 신청일 기준)
      if (app.status === '완료' && finalPrice > 0) {
        if (created.isAfter(thisMonthStart) || created.isSame(thisMonthStart)) {
          thisMonthRevenue += finalPrice;
        } else if (created.isAfter(lastMonthStart) || created.isSame(lastMonthStart)) {
          lastMonthRevenue += finalPrice;
        }
      }

      // 완료 건 + 처리시간
      if (app.status === '완료') {
        completedCount++;
        const updatedSec = app.updated_at?.seconds;
        if (updatedSec && updatedSec > createdSec) {
          const days = (updatedSec - createdSec) / 86400;
          // 비정상 데이터 제외 (30일 초과는 수정 이력 노이즈 가능성 높음)
          if (days <= 30) processingDays.push(days);
        }
      } else if (!['반송', '부분완료'].includes(app.status)) {
        inProgressCount++;
      }

      // 상태 분포
      if (app.status in statusMap) statusMap[app.status]++;

      // 일별 트렌드
      const dayKey = created.format('MM/DD');
      if (dayKey in dailyMap) dailyMap[dayKey]++;

      // 월별 매출
      if (finalPrice > 0 && app.status === '완료') {
        const monthKey = created.format('YYYY-MM');
        monthlyMap[monthKey] = (monthlyMap[monthKey] || 0) + finalPrice;
      }

      // 채널: 거래처 / 기업 / 일반고객
      const src = app.source_type || 'direct';
      const ch = app.channel || 'b2b';
      if (src === 'manual' || src === 'partner') {
        partnerCount++;
      } else if (ch === 'b2c') {
        personalCount++;
      } else {
        bizCount++;
      }

      // 기기 수
      totalDevices += app.devices?.length || 0;
    });

    const completionRate = applications.length > 0
      ? Math.round((completedCount / applications.length) * 100)
      : 0;

    const avgProcessingDays = processingDays.length > 0
      ? Math.round((processingDays.reduce((a, b) => a + b, 0) / processingDays.length) * 10) / 10
      : 0;

    return {
      total: applications.length,
      todayCount,
      yesterdayCount,
      todayChange: pctChange(todayCount, yesterdayCount),
      thisMonthRevenue,
      lastMonthRevenue,
      monthRevenueChange: pctChange(thisMonthRevenue, lastMonthRevenue),
      completionRate,
      inProgressCount,
      avgProcessingDays,
      statusDistribution: Object.entries(statusMap)
        .filter(([, v]) => v > 0)
        .map(([name, value]) => ({ name, value })),
      dailyTrend: Object.entries(dailyMap).map(([date, count]) => ({ date, count })),
      channelBreakdown: [
        { name: '거래처', value: partnerCount },
        { name: '기업', value: bizCount },
        { name: '일반고객', value: personalCount },
      ],
      monthlyRevenue: Object.entries(monthlyMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, total]) => ({ month, total })),
      totalDevices,
    };
  }, [applications]);
};
